import TextField from 'Shared/components/TextField'
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate'
import { actionTranslatablesCustomAction, actionTranslatablesDonations, actionTranslatablesEmailToTarget, actionTranslatablesShare, actionTranslatablesSurvey } from 'FlowBuilder/lib/actionTranslatable'
import { useLanguagesData, useDefaultFlowLanguage } from 'FlowBuilder/hooks/useLanguageFlow'
import './TranslationSettings.scoped.scss'
import QuestionTranslateSettings from './QuestionTranslateSettings'
import { flowTranslatablesShare } from 'FlowBuilder/lib/flowTranslatable'
import useGenericTranslate from 'FlowBuilder/hooks/useGenericTranslateFlow'
import { useAct } from 'FlowBuilder/store'
import TranslatableHtmlField from './TranslatableHtmlField'

export default function ActionTranslationSettings({ actionId, collapsed, children }) {
  const action = useSel(s => s.actions.entities[actionId || s.local.selectedActionId])
  const questions = useSel(s => _.values(s.questions.entities) || [])
  const orderedQuestions = _.orderBy(questions, 'digitOrder')

  if(!action) {
    return <></>
  }

  if (action?.kind == 'custom') {
    return <>{children}<TranslateOptions>
      {collapsed && Object.keys(actionTranslatablesCustomAction).map((strKey) => (
        <ActionTranslateSetting actionId={action.id} strKey={strKey} translatable={actionTranslatablesCustomAction[strKey]} key={strKey} />
      ))}
    </TranslateOptions></>
  }

  if (action?.kind == 'email_to_target') {
    return <>{children}<TranslateOptions>
      {collapsed && Object.keys(actionTranslatablesEmailToTarget).map((strKey) => (
        <ActionTranslateSetting actionId={action.id} strKey={strKey} translatable={actionTranslatablesEmailToTarget[strKey]} key={strKey} />
      ))}
    </TranslateOptions></>
  }

  if (action?.kind == 'donate') {
    return <>{children}<TranslateOptions>
      {collapsed && Object.keys(actionTranslatablesDonations).map((strKey) => (
        <ActionTranslateSetting actionId={action.id} strKey={strKey} translatable={actionTranslatablesDonations[strKey]} key={strKey} />
      ))}
    </TranslateOptions></>
  }

  if (action?.kind == 'survey') {
    return <>{children}<TranslateOptions>
      {collapsed && <>
        {Object.keys(actionTranslatablesSurvey).map((strKey) => (
          <ActionTranslateSetting actionId={action.id} strKey={strKey} translatable={actionTranslatablesSurvey[strKey]} key={strKey} />
        ))}
        {orderedQuestions.map(question => (
          <QuestionTranslateSettings question={question} />
        ))}
      </>}
    </TranslateOptions></>
  }

  if (action?.kind == 'share') {
    return <>{children}<TranslateOptions>
      {collapsed && <>
        {Object.keys(actionTranslatablesShare).map((strKey) => (
          <ActionTranslateSetting actionId={action.id} strKey={strKey} translatable={actionTranslatablesShare[strKey]} key={strKey} />
        ))}
      </>}
    </TranslateOptions></>
  }

  return <></>
}

export function TranslateOptions({children}) {
  const { editableLanguagesData } = useLanguagesData()

  return (
    <div
      className="translatable"
      style={{
        gridTemplateColumns: `repeat(${Object.keys(editableLanguagesData).length + 1}, 1fr)`,
      }}>
      {children}
    </div>
  )
}


function ActionTranslateSetting({ actionId, strKey, translatable }) {
  const defaultLanguage = useDefaultFlowLanguage()
  const { editableLanguagesData } = useLanguagesData()

  const {
    translated,
  } = useActionTranslate({ actionId, languageOverride: defaultLanguage })

  if (translatable.hideIfNotPresent && !translated[strKey]) {
    return null
  }

  return <>
    <div className="translatable-label">
      <span><strong>{translatable.label}</strong></span>
    </div>
    {Object.keys(editableLanguagesData).map((lang) => (
      <ActionTranslateLanguageSetting
        actionId={actionId}
        key={lang}
        strKey={strKey}
        translatable={translatable}
        languageOverride={lang}
      />
    ))}
  </>
}

function ActionTranslateLanguageSetting({ actionId, strKey, translatable, languageOverride }) {
  const act = useAct()
  const {
    translated,
    fallback,
    textContentUpdater,
    flag,
  } = useActionTranslate({ actionId, languageOverride: languageOverride })

  function openHtmlEditor() {
    act.local.setModal({
      kind: 'editContent',
      fieldKind: 'actionTextContent',
      actionId,
      slug: strKey,
      wysiwyg: true,
      language: languageOverride,
    })
  }

  return <div className={`translatable-input translatable-input-${languageOverride} translatable-input-${strKey}`}>
    <span>{flag}</span>
    {translatable.html ? (
      <TranslatableHtmlField
        value={translated[strKey] || ''}
        placeholder={fallback[strKey] || translatable.default || ''}
        onClick={openHtmlEditor}
      />
    ) : (
      <TextField
        key={strKey}
        value={translated[strKey] || ''}
        placeholder={fallback[strKey] || translatable.default || ''}
        onChange={(val) => textContentUpdater(strKey, val)}
      />
    )}
  </div>
}

const variantTranslatableKeys = ['shareTitle', 'shareDescription', 'shareReason'] as const

export function ContentVariantTranslationSettings() {
  const contentVariants = useSel(s =>
    (s.builder?.flow?.shareContentTest?.contentVariants || [])
      .filter(cv => cv.active !== false)
      .sort((a, b) => (a.content?.index ?? 0) - (b.content?.index ?? 0))
  )

  if (contentVariants.length === 0) return null

  return <>
    {contentVariants.map((variant, idx) => (
      <ContentVariantTranslateSetting key={variant.id} variant={variant} variantNumber={idx + 1} />
    ))}
  </>
}

function ContentVariantTranslateSetting({ variant, variantNumber }) {
  const { editableLanguagesData } = useLanguagesData()
  const contentVariantCount = useSel(s =>
    (s.builder?.flow?.shareContentTest?.contentVariants || []).filter(cv => cv.active !== false).length
  )

  return <>
    {variantTranslatableKeys.map((strKey) => (
      <React.Fragment key={`${variant.id}-${strKey}`}>
        <div className="translatable-label">
          <span><strong>{flowTranslatablesShare[strKey]?.label}{contentVariantCount > 1 ? ` (Variant ${variantNumber})` : ''}</strong></span>
        </div>
        {Object.keys(editableLanguagesData).map((lang) => (
          <ContentVariantLanguageSetting
            key={lang}
            variant={variant}
            strKey={strKey}
            languageOverride={lang}
          />
        ))}
      </React.Fragment>
    ))}
  </>
}

function ContentVariantLanguageSetting({ variant, strKey, languageOverride }) {
  const act = useAct()
  const { translated, fallback, flag } = useGenericTranslate({
    textContent: variant.content?.textContent || {},
    languageOverride,
  })

  function onUpdate(value) {
    act.builder.updateFlowContentTest({
      slug: 'shareContentTest',
      contentVariantId: variant.id,
      key: strKey,
      value,
      language: languageOverride,
    })
  }

  return <div className={`translatable-input translatable-input-${languageOverride} translatable-input-${strKey}`}>
    <span>{flag}</span>
    <TextField
      key={strKey}
      value={translated[strKey] || ''}
      placeholder={fallback[strKey] || flowTranslatablesShare[strKey]?.default || ''}
      onChange={onUpdate}
    />
  </div>
}
